import React, { useState, useEffect, useRef } from 'react';
import { Package, Plus, Trash2, Save, AlertCircle, CheckCircle, RefreshCw, Search, List, Check, Edit3, MapPin, ArrowRight, ChevronDown, ChevronRight, Database } from 'lucide-react';
import { pOrderLineService } from '../../../../services/pOrderLineService';
import { POrderLineDto, POrdersDto } from '../../../../types/porders';
import { StcTransLineStatus } from '../../../../types/stcTrans';
import { StcTransDto, stcTransService, CreateStcTransRequest } from '../../../../services/stcTransService';
import { pOrdersService } from '../../../../services/pOrdersService';
import { CombinedSearchBox } from './search/CombinedSearchBox';
import { POrderLineBrowser } from './search/POrderLineBrowser';
import { PutAwayModal } from './PutAwayModal';
import { StcTransEditModal } from './StcTransEditModal';
import { DirectReceiptModal } from './DirectReceiptModal';
import { parseGS1Barcode, GS1Data } from '../../../../utils/gs1Parser';
import { userProfileService } from '../../../../services/userProfileService';
import { UserProfileDto } from '../../../../types/userProfile';
import { itemsService } from '../../../../services/itemsService';
import { BackButton } from '../../../common/BackButton';
import { ReceivingActiveLine } from './ReceivingActiveLine';
import { AdvancedItemConfigModal } from './AdvancedItemConfigModal';
import { printingService } from '../../../../services/printingService';
import { useSimpleBarcodeScanner } from '../../../../hooks/useSimpleBarcodeScanner';
import { MobileBarcodeCaptureInput } from '../../../common/scanning';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface POrderReceivingDetailProps {
    orderNo: number;
    onBack: () => void;
    onNavigateToPutAway?: (stcOrderNo: number) => void;
}

interface ReceivingLine {
    orderLine: POrderLineDto;
    receivedTransactions: StcTransDto[];
    totalReceived: number;
    remainingQuantity: number;
}

interface BatchEditState {
    [transactionId: string]: {
        itemBatchNo: string;
        itemBatchExpDate: string;
        quantity: number;
        isEditing: boolean;
        isSaving: boolean;
        isEditingQuantity: boolean;
    };
}

interface OverReceiveConfirmModalProps {
    isOpen: boolean;
    message: string;
    onConfirm: () => void;
    onCancel: () => void;
}

const OverReceiveConfirmModal: React.FC<OverReceiveConfirmModalProps> = ({
    isOpen,
    message,
    onConfirm,
    onCancel
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                {/* Header */}
                <div className="bg-yellow-500 text-white px-6 py-4 rounded-t-lg">
                    <div className="flex items-center">
                        <svg className="h-6 w-6 mr-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <h3 className="text-lg font-semibold">Advarsel: Overmottak</h3>
                    </div>
                </div>

                {/* Body */}
                <div className="px-6 py-4">
                    <p className="text-gray-700 whitespace-pre-line">{message}</p>
                </div>

                {/* Footer with buttons */}
                <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end gap-3">
                    <button
                        onClick={onCancel}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors font-medium"
                    >
                        Avbryt
                    </button>
                    <button
                        onClick={onConfirm}
                        className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600 transition-colors font-medium"
                    >
                        Fortsett
                    </button>
                </div>
            </div>
        </div>
    );
};

export const POrderReceivingDetail: React.FC<POrderReceivingDetailProps> = ({ orderNo, onBack, onNavigateToPutAway }) => {
    const { t } = useTranslation();
    const [order, setOrder] = useState<POrdersDto | null>(null);
    const [receivingLines, setReceivingLines] = useState<ReceivingLine[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [newReceipt, setNewReceipt] = useState<{ lineNo: number; quantity: number; } | null>(null);
    const [batchId, setBatchId] = useState<string>('');
    const [showLineBrowser, setShowLineBrowser] = useState(false);
    const [selectedSearchLine, setSelectedSearchLine] = useState<POrderLineDto | null>(null);
    const [availableOrderLines, setAvailableOrderLines] = useState<POrderLineDto[]>([]);
    const [hasTransactions, setHasTransactions] = useState(false);
    const [canFinishReceiving, setCanFinishReceiving] = useState(false);
    const [isFinishingReceiving, setIsFinishingReceiving] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [inputJustFocused, setInputJustFocused] = useState(false);
    const [showPutAwayModal, setShowPutAwayModal] = useState(false);
    const [selectedStcTrans, setSelectedStcTrans] = useState<StcTransDto | null>(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDirectReceiptModal, setShowDirectReceiptModal] = useState(false);
    const [batchEditState, setBatchEditState] = useState<BatchEditState>({});
    const [showLockedLines, setShowLockedLines] = useState(false);
    const [editingStcTrans, setEditingStcTrans] = useState<StcTransDto | null>(null);
    const [selectedLineItemType, setSelectedLineItemType] = useState<number | undefined>(undefined);
    const [showAdvancedConfigModal, setShowAdvancedConfigModal] = useState(false);
    const [advancedConfigItemNo, setAdvancedConfigItemNo] = useState<string | null>(null);
    const [allowAdvItemConfig, setAllowAdvItemConfig] = useState(false);
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);
    const [bulkProcessingMessage, setBulkProcessingMessage] = useState('');
    const [userProfile, setUserProfile] = useState<UserProfileDto | null>(null);
    const [warningMessage, setWarningMessage] = useState<string>('');
    const [validationError, setValidationError] = useState<string>('');
    const [validationWarning, setValidationWarning] = useState<string>('');
    const [showOverReceiveConfirmModal, setShowOverReceiveConfirmModal] = useState(false);
    const [pendingOverReceiveAction, setPendingOverReceiveAction] = useState<(() => Promise<void>) | null>(null);
    const [overReceiveModalMessage, setOverReceiveModalMessage] = useState('');
    const quantityInputRef = useRef<HTMLInputElement>(null);
    const scannerRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        loadOrderData();
    }, [orderNo]);

    // Load user profile to check advanced config permission
    useEffect(() => {
        const loadUserProfile = async () => {
            try {
                const profile = await userProfileService.getCurrentUserProfile();
                console.log('🔍 User Profile Loaded:', profile);
                console.log('🔍 extraQtyOnGRNotAllowed:', profile.extraQtyOnGRNotAllowed);
                console.log('🔍 extraQtyOnGRConfirm:', profile.extraQtyOnGRConfirm);
                setUserProfile(profile);  // Store full profile
                setAllowAdvItemConfig(profile.allowAdvItemConfigYesNo || false);
            } catch (error) {
                console.error('Error loading user profile:', error);
                setAllowAdvItemConfig(false);
            }
        };
        loadUserProfile();
    }, []);

    // Fetch itemType when selectedSearchLine changes
    useEffect(() => {
        const fetchItemType = async () => {
            if (selectedSearchLine && order) {
                try {
                    const item = await itemsService.getItemByCompanyAndItemNo(
                        order.companyNo,
                        selectedSearchLine.itemNo
                    );
                    setSelectedLineItemType(item.itemType);
                } catch (error) {
                    console.warn('Could not fetch itemType for selected line:', error);
                    setSelectedLineItemType(undefined);
                }
            } else {
                setSelectedLineItemType(undefined);
            }
        };
        fetchItemType();
    }, [selectedSearchLine, order]);

    // Auto-focus disabled - global scanning is active
    // useEffect(() => {
    //     if (newReceipt && quantityInputRef.current) {
    //         quantityInputRef.current.focus();
    //         const input = quantityInputRef.current;
    //         setTimeout(() => {
    //             const length = input.value.length;
    //             input.setSelectionRange(length, length);
    //         }, 0);
    //     }
    // }, [newReceipt]);

    const checkCanFinishReceiving = async (batchId: string) => {
        try {
            console.log('Checking if receiving can be finished for batch:', batchId);
            const result = await stcTransService.canFinishReceiving(batchId);
            console.log('Can finish receiving result:', result);
            setCanFinishReceiving(result.canFinish);
            if (!result.canFinish) {
                console.log('Cannot finish receiving:', result.message);
            }
        } catch (error) {
            console.error('Error checking if receiving can be finished:', error);
            setCanFinishReceiving(false);
        }
    };

    const handleFinishReceiving = async () => {
        if (!batchId) return;

        try {
            setIsFinishingReceiving(true);
            await stcTransService.finishReceiving(batchId);

            setShowSuccessMessage(true);
            setError(null);

            setTimeout(() => {
                onBack();
            }, 2000);

        } catch (error) {
            console.error('Error finishing receiving:', error);
            setError(`Kunne ikke avslutte mottak: ${error instanceof Error ? error.message : 'Ukjent feil'}`);
        } finally {
            setIsFinishingReceiving(false);
        }
    };

    const handleFinishReceivingAndPutAway = async () => {
        if (!batchId || !order) return;

        try {
            setIsFinishingReceiving(true);
            setError(null);

            // Call the new endpoint to finish receiving and generate put-away tasks
            const result = await stcTransService.finishReceivingAndPutAway(batchId, {
                orderNo: order.orderNo
            });

            if (result.success) {
                setShowSuccessMessage(true);

                // Show messages from backend
                if (result.messages && result.messages.length > 0) {
                    console.log('Process messages:', result.messages);
                }

                // Navigate to put-away module after a short delay
                setTimeout(() => {
                    if (onNavigateToPutAway && result.stcOrderNo) {
                        onNavigateToPutAway(result.stcOrderNo);
                    } else if (onNavigateToPutAway) {
                        // If no stcOrderNo but function exists, call without parameter (will show list)
                        console.warn('No StcOrderNo received from backend, navigating to put-away list');
                        onNavigateToPutAway(0);
                    } else {
                        // Fallback to going back if navigation function not provided
                        onBack();
                    }
                }, 1500);
            } else {
                // Handle partial success or failure
                if (result.receivingFinished && !result.putAwayTasksGenerated) {
                    setError('Mottak avsluttet, men plasseringsoppgaver ble ikke opprettet. Kontakt administrator.');
                } else {
                    setError(result.errorMessage || 'Kunne ikke fullføre operasjonen.');
                }
            }

        } catch (error) {
            console.error('Error finishing receiving and generating put-away:', error);
            setError(`Kunne ikke fullføre operasjonen: ${error instanceof Error ? error.message : 'Ukjent feil'}`);
        } finally {
            setIsFinishingReceiving(false);
        }
    };

    const handleOpenAdvancedConfig = (itemNo: string) => {
        setAdvancedConfigItemNo(itemNo);
        setShowAdvancedConfigModal(true);
    };

    const handleCloseAdvancedConfig = () => {
        setShowAdvancedConfigModal(false);
        setAdvancedConfigItemNo(null);
    };

    const handleAdvancedConfigSaved = () => {
        // Refresh order data after saving config
        loadOrderData();
    };

    const loadOrderData = async (bustCache: boolean = false) => {
        try {
            setLoading(true);
            setError(null);

            console.log('Henting ordre-informasjon for orderNo:', orderNo, bustCache ? '(cache-busting enabled)' : '');
            const orderData = await pOrdersService.getOrderByOrderNo(orderNo);
            setOrder(orderData);

            console.log('Henting ordrelinjer for orderNo:', orderNo);
            let orderLines;
            try {
                orderLines = await pOrderLineService.getOrderLines(orderNo);
            } catch (lineError) {
                console.warn('Kunne ikke hente ordrelinjer direkte, prøver via details:', lineError);
                try {
                    const orderDetails = await pOrderLineService.getOrderDetails(orderNo);
                    orderLines = orderDetails.lines;
                } catch (detailsError) {
                    throw new Error(`Kunne ikke hente ordrelinjer: ${lineError instanceof Error ? lineError.message : 'Ukjent feil'}`);
                }
            }

            if (!orderLines || orderLines.length === 0) {
                console.warn('Ingen ordrelinjer funnet for ordre', orderNo);
                setReceivingLines([]);
                setLoading(false);
                return;
            }

            const usedBatchId = await stcTransService.getOrCreateBatchId(orderNo);
            setBatchId(usedBatchId);

            const receivingLineData: ReceivingLine[] = await Promise.all(
                orderLines.map(async (line) => {
                    try {
                        const transactions = await stcTransService.getTransactionsByBatch(usedBatchId, bustCache);
                        console.log(`Loaded ${transactions.length} transactions for batch ${usedBatchId}${bustCache ? ' (cache busted)' : ''}`);
                        // Hent ALLE transaksjoner (inkludert låste med status >= 50)
                        const lineTransactions = transactions.filter(t => t.lineNo === line.lineNo);
                        console.log(`Line ${line.lineNo}: found ${lineTransactions.length} transactions`, lineTransactions.map(t => ({ id: t.id, qty: t.quantity, batch: t.itemBatchNo })));
                        // For beregning av "mottatt" teller vi kun aktive transaksjoner (status < 50)
                        const activeLineTransactions = lineTransactions.filter(t => t.lineStatus < 50);
                        const stcTransReceived = activeLineTransactions.reduce((sum, t) => sum + (t.quantity || 0), 0);
                        
                        // Total mottatt = tidligere ferdigstillte mottak + aktive StcTrans
                        const totalReceived = (line.quantityDelivered || 0) + stcTransReceived;
                        
                        // Gjenværende = bestilt - total mottatt
                        const remainingQuantity = (line.quantity || 0) - totalReceived;

                        return {
                            orderLine: line,
                            receivedTransactions: lineTransactions,
                            totalReceived,
                            remainingQuantity
                        };
                    } catch (err) {
                        console.log('Ingen eksisterende mottakslinjer funnet for batch-ID:', usedBatchId);
                        
                        // Fallback når ingen StcTrans finnes - bruk kun ordrelinje data
                        const totalReceived = line.quantityDelivered || 0;
                        const remainingQuantity = (line.quantity || 0) - totalReceived;
                        
                        return {
                            orderLine: line,
                            receivedTransactions: [],
                            totalReceived,
                            remainingQuantity
                        };
                    }
                })
            );

            setReceivingLines(receivingLineData);
            setAvailableOrderLines(orderLines);
            
            const totalTransactions = receivingLineData.reduce((sum, line) => sum + line.receivedTransactions.length, 0);
            setHasTransactions(totalTransactions > 0);

            // Sjekk om det finnes minst én transaksjon med LineStatus = 20 (Ankommet/UnderArbeid)
            const hasArrivedTransactions = receivingLineData.some(line =>
                line.receivedTransactions.some(trans => Number(trans.lineStatus) === StcTransLineStatus.UnderArbeid)
            );

            if (totalTransactions > 0) {
                // Start mottak hvis ikke allerede startet
                try {
                    await stcTransService.startReceiving(usedBatchId);
                } catch (startError) {
                    console.log('Could not start receiving (may already be started):', startError);
                }

                // Aktiver ferdigstill-knappene hvis det finnes ankomne linjer (LineStatus = 20)
                if (hasArrivedTransactions) {
                    // Valider via backend for å sikre at alle krav er oppfylt (bins, batch-numre, etc.)
                    await checkCanFinishReceiving(usedBatchId);
                } else {
                    setCanFinishReceiving(false);
                }
            } else {
                setCanFinishReceiving(false);
            }
            
            setLoading(false);
        } catch (err) {
            console.error('Detaljert feil ved henting av ordre-data:', err);
            setError(`Kunne ikke hente ordre-informasjon: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
            setLoading(false);
        }
    };

    /**
     * Helper function that performs the actual save operation.
     * Extracted to allow pre-confirmation via modal.
     */
    const proceedWithSave = async (
        lineNo: number,
        quantity: number,
        batchNo?: string,
        expDate?: string,
        receivingLine?: ReceivingLine,
        receivingLocation?: string
    ) => {
        if (!order) return;

        try {
            if (!receivingLine) return;

            // Check if we're editing an existing StcTrans or creating a new one
            if (editingStcTrans) {
                // Update existing StcTrans
                const updateRequest = {
                    batchID: editingStcTrans.batchID,
                    lineType: editingStcTrans.lineType,
                    orderNo: editingStcTrans.orderNo,
                    lineNo: editingStcTrans.lineNo,
                    locationName: editingStcTrans.locationName,
                    quantity: quantity,
                    transDt: editingStcTrans.transDt,
                    warehouse: editingStcTrans.warehouse,
                    toWarehouse: editingStcTrans.toWarehouse,
                    operatorID: editingStcTrans.operatorID,
                    profileID: editingStcTrans.profileID,
                    exportedYesNo: editingStcTrans.exportedYesNo,
                    lineStatus: editingStcTrans.lineStatus,
                    itemNo: editingStcTrans.itemNo,
                    itemUnitCode: editingStcTrans.itemUnitCode,
                    itemBarCode: editingStcTrans.itemBarCode,
                    description1: editingStcTrans.description1,
                    itemBatchNo: batchNo?.trim() || editingStcTrans.itemBatchNo || undefined,
                    itemBatchExpDate: expDate ? new Date(expDate) : editingStcTrans.itemBatchExpDate,
                    itemSerialNo: editingStcTrans.itemSerialNo,
                    erpLinkName: editingStcTrans.erpLinkName,
                    shipmentNo: editingStcTrans.shipmentNo,
                    quantityOrigin: editingStcTrans.quantityOrigin,
                    itemUnitTxt: editingStcTrans.itemUnitTxt,
                    lineTypeOrigin: editingStcTrans.lineTypeOrigin,
                    price: editingStcTrans.price,
                    currency: editingStcTrans.currency,
                    reasonCode: editingStcTrans.reasonCode,
                    transType: editingStcTrans.transType,
                    sscc: editingStcTrans.sscc,
                    quantityCorrected: editingStcTrans.quantityCorrected,
                    freeField1: editingStcTrans.freeField1,
                    freeField2: editingStcTrans.freeField2,
                    freeField3: editingStcTrans.freeField3,
                    appFreeField1: editingStcTrans.appFreeField1,
                    appFreeField2: editingStcTrans.appFreeField2,
                    quantityGross: editingStcTrans.quantityGross,
                    transitPackagingID: editingStcTrans.transitPackagingID,
                    clientID: editingStcTrans.clientID,
                    stcBoxNo: editingStcTrans.stcBoxNo,
                    grShipmentNo: editingStcTrans.grShipmentNo,
                    externalSystemID: editingStcTrans.externalSystemID,
                    externalSystemStatus: editingStcTrans.externalSystemStatus,
                    itemType: editingStcTrans.itemType,
                    orderNoERP: editingStcTrans.orderNoERP,
                    lineNoERP: editingStcTrans.lineNoERP
                };

                try {
                    await stcTransService.updateTransaction(editingStcTrans.batchID, editingStcTrans.lineID, updateRequest);
                    console.log('Transaction updated successfully');
                } catch (updateError: any) {
                    console.log('PUT not supported, falling back to delete/create method');
                    await stcTransService.deleteTransaction(editingStcTrans.batchID, editingStcTrans.lineID);
                    await stcTransService.createTransaction(editingStcTrans.batchID, updateRequest);
                    console.log('Transaction updated using fallback method');
                }
            } else {
                // Create new StcTrans
                await stcTransService.ensureTransOrderExists(batchId, order);

                // Use passed receivingLocation or fall back to profile default
                let locationName = receivingLocation || '';
                if (!locationName) {
                    try {
                        const profile = await userProfileService.getCurrentUserProfile();
                        locationName = profile.defaultGRBinCode || '';
                    } catch (profileError) {
                        console.warn('Kunne ikke hente profil for LocationName:', profileError);
                    }
                }

                let itemType: number | undefined;
                try {
                    const item = await itemsService.getItemByCompanyAndItemNo(
                        order.companyNo,
                        receivingLine.orderLine.itemNo
                    );
                    itemType = item.itemType;
                } catch (itemError) {
                    console.warn('Kunne ikke hente item for ItemType:', itemError);
                }

                const erpLinkName = order.orderNoERP + '-' + receivingLine.orderLine.lineNoERP;

                const newTransaction: CreateStcTransRequest = {
                    batchID: batchId,
                    lineType: 0,
                    orderNo: orderNo,
                    lineNo: lineNo,
                    locationName: locationName,
                    quantity: quantity,
                    transDt: new Date(),
                    warehouse: order.warehouse || 'MAIN',
                    lineStatus: StcTransLineStatus.UnderArbeid,
                    itemNo: receivingLine.orderLine.itemNo,
                    itemBarCode: receivingLine.orderLine.itemBarCode,
                    description1: receivingLine.orderLine.description1,
                    itemUnitCode: receivingLine.orderLine.itemUnitCode,
                    itemUnitTxt: receivingLine.orderLine.itemUnitTxt,
                    transType: 0,
                    orderNoERP: order.orderNoERP,
                    lineNoERP: receivingLine.orderLine.lineNoERP,
                    erpLinkName: erpLinkName,
                    itemType: itemType,
                    itemBatchNo: batchNo?.trim() || undefined,
                    itemBatchExpDate: expDate ? new Date(expDate) : undefined
                };

                console.log('Creating new StcTrans with batch info:', { batchNo, expDate });
                await stcTransService.createTransaction(batchId, newTransaction);
            }

            // Clear all editing states FIRST to prevent stale state conflicts
            console.log('Clearing editing states before reload');
            setNewReceipt(null);
            setSelectedSearchLine(null);
            setEditingStcTrans(null);
            setBatchEditState({}); // Reset batch edit state to force re-initialization from fresh data
            setWarningMessage('');
            setValidationError('');
            setValidationWarning('');
            setError(null);

            // Reload data to refresh the list with updated values
            console.log('Reloading order data after save with cache busting');

            // Add delay to ensure database transaction is committed and events processed
            await new Promise(resolve => setTimeout(resolve, 300));

            // Force cache bust to ensure we get fresh data
            await loadOrderData(true);

            console.log('Order data reloaded, receivingLines should now show updated values');

            // Re-check if receiving can be finished after adding/updating receipt
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Error adding/updating receipt:', err);
            setError(t('warehouse.modules.receipt.errors.saveReceiptFailed', { error: err instanceof Error ? err.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke lagre mottak: ${err instanceof Error ? err.message : 'Ukjent feil'}`));
        }
    };

    const handleAddReceipt = async (lineNo: number, quantity: number, batchNo?: string, expDate?: string, receivingLocation?: string) => {
        if (!order) return;

        // Validate quantity is positive
        if (quantity <= 0) {
            setError(t('warehouse.modules.receipt.validation.quantityMustBePositive', {}, 'Antall må være større enn 0'));
            return;
        }

        // Validate over-receiving
        const receivingLine = receivingLines.find(rl => rl.orderLine.lineNo === lineNo);
        const orderLine = receivingLine?.orderLine;

        if (orderLine) {
            const validation = validateOverReceiving(orderLine, quantity, receivingLine);
            console.log('🔍 Validation result:', validation);

            if (!validation.allowed) {
                console.log('🚫 Blocking save - setting error');
                // BLOCKED - Show error inline and return
                setValidationError(validation.message);
                setValidationWarning('');
                return;
            } else if (validation.isWarning) {
                console.log('⚠️ Showing warning modal - waiting for user confirmation');
                // WARNING - Show confirmation modal BEFORE proceeding
                setOverReceiveModalMessage(validation.message);
                setPendingOverReceiveAction(() => async () => {
                    // This function will be called if user clicks "Fortsett"
                    await proceedWithSave(lineNo, quantity, batchNo, expDate, receivingLine, receivingLocation);
                });
                setShowOverReceiveConfirmModal(true);
                return; // Stop here, wait for user decision
            } else {
                console.log('✅ No issues - clearing messages');
                // Clear any previous warnings
                setValidationError('');
                setValidationWarning('');
            }
        }

        // If we get here, validation passed without warning - proceed directly
        await proceedWithSave(lineNo, quantity, batchNo, expDate, receivingLine, receivingLocation);
    };

    const handleDeleteReceipt = async (lineID: number, transactionId: number) => {
        try {
            await stcTransService.deleteTransaction(batchId, lineID);
            await loadOrderData();

            // Clear active line state to return to main view
            setNewReceipt(null);
            setSelectedSearchLine(null);
            setEditingStcTrans(null);
            setValidationError('');
            setValidationWarning('');
            setError(null);

            // Re-check if receiving can be finished after deleting receipt
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Error deleting receipt:', err);
            setError(`Kunne ikke slette mottak: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
        }
    };

    const handlePutAway = (stcTrans: StcTransDto) => {
        setSelectedStcTrans(stcTrans);
        setShowPutAwayModal(true);
    };

    const handlePutAwayAll = () => {
        // For now, we'll use the first available StcTrans as a representative transaction
        // This could be enhanced to handle multiple transactions or create a batch put-away
        const allTransactions = receivingLines.flatMap(line => line.receivedTransactions);
        if (allTransactions.length > 0) {
            setSelectedStcTrans(allTransactions[0]); // Use first transaction as representative
            setShowPutAwayModal(true);
        }
    };

    const handleEditStcTrans = (stcTrans: StcTransDto) => {
        setSelectedStcTrans(stcTrans);
        setShowEditModal(true);
    };

    const handleDirectReceipt = (stcTrans: StcTransDto) => {
        setSelectedStcTrans(stcTrans);
        setShowDirectReceiptModal(true);
    };

    /**
     * Helper function that performs the actual direct receipt creation.
     * Extracted to allow pre-confirmation via modal.
     */
    const proceedWithDirectReceipt = async (
        lineNo: number,
        quantity: number,
        batchNo?: string,
        expDate?: string,
        receivingLine?: ReceivingLine,
        receivingLocation?: string
    ) => {
        if (!order) return;

        try {
            if (!receivingLine) {
                setError('Finner ikke ordrelinje');
                return;
            }

            // Valider batch-info hvis nødvendig (ItemType 1 eller 4)
            let itemType: number | undefined;
            try {
                const item = await itemsService.getItemByCompanyAndItemNo(
                    order.companyNo,
                    receivingLine.orderLine.itemNo
                );
                itemType = item.itemType;

                // Valider batch-nummer for ItemType 1 og 4
                if ((itemType === 1 || itemType === 4) && !batchNo) {
                    setError('Batchnummer er påkrevd for denne varen');
                    return;
                }

                // Valider utløpsdato for ItemType 4
                if (itemType === 4 && !expDate) {
                    setError('Utløpsdato er påkrevd for denne varen');
                    return;
                }
            } catch (itemError) {
                console.warn('Kunne ikke hente item for ItemType-validering:', itemError);
            }

            // Opprett StcTrans først (samme logikk som handleAddReceipt)
            await stcTransService.ensureTransOrderExists(batchId, order);

            // Use passed receivingLocation or fall back to profile default
            let locationName = receivingLocation || '';
            if (!locationName) {
                try {
                    const profile = await userProfileService.getCurrentUserProfile();
                    locationName = profile.defaultGRBinCode || '';
                } catch (profileError) {
                    console.warn('Kunne ikke hente profil for LocationName:', profileError);
                }
            }

            const erpLinkName = order.orderNoERP + '-' + receivingLine.orderLine.lineNoERP;

            const newTransaction: CreateStcTransRequest = {
                batchID: batchId,
                lineType: 0,
                orderNo: orderNo,
                lineNo: lineNo,
                locationName: locationName,
                quantity: quantity,
                transDt: new Date(),
                warehouse: order.warehouse || 'MAIN',
                lineStatus: StcTransLineStatus.UnderArbeid,
                itemNo: receivingLine.orderLine.itemNo,
                itemBarCode: receivingLine.orderLine.itemBarCode,
                description1: receivingLine.orderLine.description1,
                itemUnitCode: receivingLine.orderLine.itemUnitCode,
                itemUnitTxt: receivingLine.orderLine.itemUnitTxt,
                transType: 0,
                orderNoERP: order.orderNoERP,
                lineNoERP: receivingLine.orderLine.lineNoERP,
                erpLinkName: erpLinkName,
                itemType: itemType,
                itemBatchNo: batchNo,
                itemBatchExpDate: expDate ? new Date(expDate) : undefined
            };

            console.log('Oppretter StcTrans for direktemottak:', newTransaction);
            const createdStcTrans = await stcTransService.createTransaction(batchId, newTransaction);

            console.log('StcTrans opprettet, åpner direktemottak-modal');

            // Nå åpne direktemottak-modalen med den nyopprettede StcTrans
            setSelectedStcTrans(createdStcTrans);
            setShowDirectReceiptModal(true);

            // Clear aktiv linje state and messages
            setNewReceipt(null);
            setSelectedSearchLine(null);
            setWarningMessage('');
            setValidationError('');
            setValidationWarning('');
            setError(null);

        } catch (err) {
            console.error('Error creating StcTrans for direct receipt:', err);
            setError(`Kunne ikke opprette mottak: ${err instanceof Error ? err.message : 'Ukjent feil'}`);
        }
    };

    /**
     * Håndterer direktemottak fra "Aktiv linje" når vi oppretter NYE mottak.
     * Følger legacy-pattern: Opprett StcTrans først, deretter åpne direktemottak-modal.
     */
    const handleDirectReceiptFromNew = async (lineNo: number, quantity: number, batchNo?: string, expDate?: string, receivingLocation?: string) => {
        if (!order || !newReceipt) return;

        setError(null);

        const receivingLine = receivingLines.find(rl => rl.orderLine.lineNo === lineNo);
        if (!receivingLine) {
            setError('Finner ikke ordrelinje');
            return;
        }

        // Valider antall
        if (quantity <= 0) {
            setError(t('warehouse.modules.receipt.validation.quantityMustBePositive', {}, 'Antall må være større enn 0'));
            return;
        }

        // Validate over-receiving
        const validation = validateOverReceiving(receivingLine.orderLine, quantity, receivingLine);
        console.log('🔍 Direct Receipt Validation result:', validation);

        if (!validation.allowed) {
            console.log('🚫 Blocking direct receipt - setting error');
            // BLOCKED - Show error inline and return
            setValidationError(validation.message);
            setValidationWarning('');
            return;
        } else if (validation.isWarning) {
            console.log('⚠️ Showing warning modal for direct receipt - waiting for user confirmation');
            // WARNING - Show confirmation modal BEFORE proceeding
            setOverReceiveModalMessage(validation.message);
            setPendingOverReceiveAction(() => async () => {
                // This function will be called if user clicks "Fortsett"
                await proceedWithDirectReceipt(lineNo, quantity, batchNo, expDate, receivingLine, receivingLocation);
            });
            setShowOverReceiveConfirmModal(true);
            return; // Stop here, wait for user decision
        } else {
            console.log('✅ No issues - clearing messages');
            // Clear any previous warnings
            setValidationError('');
            setValidationWarning('');
        }

        // If we get here, validation passed without warning - proceed directly
        await proceedWithDirectReceipt(lineNo, quantity, batchNo, expDate, receivingLine, receivingLocation);
    };

    const handleModalClose = () => {
        setShowPutAwayModal(false);
        setShowEditModal(false);
        setShowDirectReceiptModal(false);
        setSelectedStcTrans(null);
    };

    const handleModalSuccess = async () => {
        await loadOrderData();
        handleModalClose();

        // Clear active line state to return to main view
        setNewReceipt(null);
        setSelectedSearchLine(null);
        setEditingStcTrans(null);
        setValidationError('');
        setValidationWarning('');
        setError(null);

        // Re-check if receiving can be finished after modal operations
        if (batchId) {
            await checkCanFinishReceiving(batchId);
        }
    };

    const handleConfirmOverReceive = async () => {
        console.log('✅ User confirmed over-receive');
        setShowOverReceiveConfirmModal(false);

        if (pendingOverReceiveAction) {
            await pendingOverReceiveAction();
            setPendingOverReceiveAction(null);
        }
        setOverReceiveModalMessage('');
    };

    const handleCancelOverReceive = () => {
        console.log('❌ User cancelled over-receive');
        setShowOverReceiveConfirmModal(false);
        setPendingOverReceiveAction(null);
        setOverReceiveModalMessage('');
        // Keep data intact - user can modify quantity and retry
    };

    const initializeBatchEdit = (trans: StcTransDto) => {
        const transId = `${trans.id}`;
        if (!batchEditState[transId]) {
            setBatchEditState(prev => ({
                ...prev,
                [transId]: {
                    itemBatchNo: trans.itemBatchNo || '',
                    itemBatchExpDate: trans.itemBatchExpDate ? new Date(trans.itemBatchExpDate).toISOString().split('T')[0] : '',
                    quantity: trans.quantity || 0, // Bruk faktisk quantity fra StcTrans
                    isEditing: false,
                    isSaving: false,
                    isEditingQuantity: false
                }
            }));
        }
    };
    
    const handleBatchFieldChange = (trans: StcTransDto, field: 'itemBatchNo' | 'itemBatchExpDate', value: string) => {
        const transId = `${trans.id}`;
        setBatchEditState(prev => ({
            ...prev,
            [transId]: {
                ...prev[transId],
                [field]: value,
                isEditing: true
            }
        }));
    };
    
    const handleQuantityChange = (trans: StcTransDto, value: number) => {
        const transId = `${trans.id}`;
        setBatchEditState(prev => ({
            ...prev,
            [transId]: {
                ...prev[transId],
                quantity: value,
                isEditingQuantity: true
            }
        }));
    };
    
    const handleQuantitySave = async (trans: StcTransDto) => {
        const transId = `${trans.id}`;
        const editState = batchEditState[transId];
        
        if (!editState || !editState.isEditingQuantity) {
            return; // Ingen endringer å lagre
        }
        
        if (editState.quantity <= 0) {
            setError(t('warehouse.modules.receipt.validation.quantityMustBePositive', {}, 'Antall må være større enn 0'));
            return;
        }
        
        try {
            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: true }
            }));
            
            // Use the new updateTransaction method
            const updateRequest = {
                batchID: trans.batchID,
                lineType: trans.lineType,
                orderNo: trans.orderNo,
                lineNo: trans.lineNo,
                locationName: trans.locationName,
                quantity: editState.quantity,
                transDt: trans.transDt,
                warehouse: trans.warehouse,
                toWarehouse: trans.toWarehouse,
                operatorID: trans.operatorID,
                profileID: trans.profileID,
                exportedYesNo: trans.exportedYesNo,
                lineStatus: trans.lineStatus,
                itemNo: trans.itemNo,
                itemUnitCode: trans.itemUnitCode,
                itemBarCode: trans.itemBarCode,
                description1: trans.description1,
                itemBatchNo: trans.itemBatchNo,
                itemBatchExpDate: trans.itemBatchExpDate,
                itemSerialNo: trans.itemSerialNo,
                erpLinkName: trans.erpLinkName,
                shipmentNo: trans.shipmentNo,
                quantityOrigin: trans.quantityOrigin,
                itemUnitTxt: trans.itemUnitTxt,
                lineTypeOrigin: trans.lineTypeOrigin,
                price: trans.price,
                currency: trans.currency,
                reasonCode: trans.reasonCode,
                transType: trans.transType,
                sscc: trans.sscc,
                quantityCorrected: trans.quantityCorrected,
                freeField1: trans.freeField1,
                freeField2: trans.freeField2,
                freeField3: trans.freeField3,
                appFreeField1: trans.appFreeField1,
                appFreeField2: trans.appFreeField2,
                quantityGross: trans.quantityGross,
                transitPackagingID: trans.transitPackagingID,
                clientID: trans.clientID,
                stcBoxNo: trans.stcBoxNo,
                grShipmentNo: trans.grShipmentNo,
                externalSystemID: trans.externalSystemID,
                externalSystemStatus: trans.externalSystemStatus,
                itemType: trans.itemType,
                orderNoERP: trans.orderNoERP,
                lineNoERP: trans.lineNoERP
            };
            
            try {
                await stcTransService.updateTransaction(trans.batchID, trans.lineID, updateRequest);
                console.log('Transaction updated successfully using PUT method');
            } catch (updateError: any) {
                // Fallback to delete/create if PUT is not supported
                console.log('PUT not supported, falling back to delete/create method');
                await stcTransService.deleteTransaction(trans.batchID, trans.lineID);
                await stcTransService.createTransaction(trans.batchID, updateRequest);
                console.log('Transaction updated using fallback delete/create method');
            }
            
            // Mark as saved
            setBatchEditState(prev => ({
                ...prev,
                [transId]: {
                    ...prev[transId],
                    isEditingQuantity: false,
                    isSaving: false
                }
            }));

            // Reload data to show changes
            await loadOrderData();

            // Re-check if receiving can be finished after updating quantity
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Error updating transaction quantity:', err);
            setError(t('warehouse.modules.receipt.errors.updateQuantityFailed', { error: err instanceof Error ? err.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke oppdatere antall: ${err instanceof Error ? err.message : 'Ukjent feil'}`));

            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: false }
            }));
        }
    };

    const handleSaveAllChanges = async (trans: StcTransDto) => {
        const transId = `${trans.id}`;
        const editState = batchEditState[transId];
        
        if (!editState || (!editState.isEditing && !editState.isEditingQuantity)) {
            return; // Ingen endringer å lagre
        }
        
        try {
            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: true }
            }));
            
            // Valider påkrevde felt før lagring
            if ((trans.itemType === 1 || trans.itemType === 4) && !editState.itemBatchNo.trim()) {
                setError(t('warehouse.modules.receipt.validation.batchRequiredForItemType', {}, 'Batchnummer er påkrevd for denne varetypen'));
                setBatchEditState(prev => ({
                    ...prev,
                    [transId]: { ...prev[transId], isSaving: false }
                }));
                return;
            }
            
            if (trans.itemType === 4 && !editState.itemBatchExpDate) {
                setError(t('warehouse.modules.receipt.validation.expiryRequiredForItemType', {}, 'Utgått dato er påkrevd for denne varetypen'));
                setBatchEditState(prev => ({
                    ...prev,
                    [transId]: { ...prev[transId], isSaving: false }
                }));
                return;
            }
            
            if (editState.quantity <= 0) {
                setError(t('warehouse.modules.receipt.validation.quantityMustBePositive', {}, 'Antall må være større enn 0'));
                setBatchEditState(prev => ({
                    ...prev,
                    [transId]: { ...prev[transId], isSaving: false }
                }));
                return;
            }
            
            // Lag complete update request
            const updateRequest = {
                batchID: trans.batchID,
                lineType: trans.lineType,
                orderNo: trans.orderNo,
                lineNo: trans.lineNo,
                locationName: trans.locationName,
                quantity: editState.quantity,
                transDt: trans.transDt,
                warehouse: trans.warehouse,
                toWarehouse: trans.toWarehouse,
                operatorID: trans.operatorID,
                profileID: trans.profileID,
                exportedYesNo: trans.exportedYesNo,
                lineStatus: trans.lineStatus,
                itemNo: trans.itemNo,
                itemUnitCode: trans.itemUnitCode,
                itemBarCode: trans.itemBarCode,
                description1: trans.description1,
                itemBatchNo: editState.itemBatchNo.trim() || undefined,
                itemBatchExpDate: editState.itemBatchExpDate ? new Date(editState.itemBatchExpDate) : undefined,
                itemSerialNo: trans.itemSerialNo,
                erpLinkName: trans.erpLinkName,
                shipmentNo: trans.shipmentNo,
                quantityOrigin: trans.quantityOrigin,
                itemUnitTxt: trans.itemUnitTxt,
                lineTypeOrigin: trans.lineTypeOrigin,
                price: trans.price,
                currency: trans.currency,
                reasonCode: trans.reasonCode,
                transType: trans.transType,
                sscc: trans.sscc,
                quantityCorrected: trans.quantityCorrected,
                freeField1: trans.freeField1,
                freeField2: trans.freeField2,
                freeField3: trans.freeField3,
                appFreeField1: trans.appFreeField1,
                appFreeField2: trans.appFreeField2,
                quantityGross: trans.quantityGross,
                transitPackagingID: trans.transitPackagingID,
                clientID: trans.clientID,
                stcBoxNo: trans.stcBoxNo,
                grShipmentNo: trans.grShipmentNo,
                externalSystemID: trans.externalSystemID,
                externalSystemStatus: trans.externalSystemStatus,
                itemType: trans.itemType,
                orderNoERP: trans.orderNoERP,
                lineNoERP: trans.lineNoERP
            };
            
            try {
                await stcTransService.updateTransaction(trans.batchID, trans.lineID, updateRequest);
                console.log('All changes saved successfully using PUT method');
            } catch (updateError: any) {
                // Fallback to delete/create if PUT is not supported
                console.log('PUT not supported, falling back to delete/create method');
                await stcTransService.deleteTransaction(trans.batchID, trans.lineID);
                await stcTransService.createTransaction(trans.batchID, updateRequest);
                console.log('All changes saved using fallback delete/create method');
            }
            
            // Mark as saved
            setBatchEditState(prev => ({
                ...prev,
                [transId]: {
                    ...prev[transId],
                    isEditing: false,
                    isEditingQuantity: false,
                    isSaving: false
                }
            }));

            // Reload data to show changes
            await loadOrderData();

            // Re-check if receiving can be finished after saving all changes
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Error saving all changes:', err);
            setError(t('warehouse.modules.receipt.errors.saveChangesFailed', { error: err instanceof Error ? err.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke lagre endringer: ${err instanceof Error ? err.message : 'Ukjent feil'}`));

            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: false }
            }));
        }
    };

    const handleBatchFieldSave = async (trans: StcTransDto, field: 'itemBatchNo' | 'itemBatchExpDate') => {
        const transId = `${trans.id}`;
        const editState = batchEditState[transId];
        
        if (!editState || !editState.isEditing) {
            return; // Ingen endringer å lagre
        }
        
        // Valider påkrevde felt
        if ((trans.itemType === 1 || trans.itemType === 4) && !editState.itemBatchNo.trim()) {
            setError('Batchnummer er påkrevd for denne varetypen');
            return;
        }
        
        if (trans.itemType === 4 && !editState.itemBatchExpDate) {
            setError('Utgått dato er påkrevd for denne varetypen');
            return;
        }
        
        try {
            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: true }
            }));
            
            // Prøv å bruke den nye updateBatchInfo metoden først
            try {
                await stcTransService.updateBatchInfo(trans.batchID, trans.lineID, {
                    itemBatchNo: editState.itemBatchNo.trim() || undefined,
                    itemBatchExpDate: editState.itemBatchExpDate ? new Date(editState.itemBatchExpDate) : undefined
                });
                
                console.log('Batch info updated successfully using PATCH method');
                
            } catch (updateError: any) {
                // Hvis PATCH ikke er støttet, fall tilbake til delete/create
                if (updateError.message === 'UPDATE_NOT_SUPPORTED') {
                    console.log('PATCH not supported, falling back to delete/create method');
                    
                    // Create updated transaction object (fallback method)
                    const updateRequest = {
                        batchID: trans.batchID,
                        lineType: trans.lineType,
                        orderNo: trans.orderNo,
                        lineNo: trans.lineNo,
                        locationName: trans.locationName,
                        quantity: trans.quantity,
                        transDt: trans.transDt,
                        warehouse: trans.warehouse,
                        toWarehouse: trans.toWarehouse,
                        operatorID: trans.operatorID,
                        profileID: trans.profileID,
                        exportedYesNo: trans.exportedYesNo,
                        lineStatus: trans.lineStatus,
                        itemNo: trans.itemNo,
                        itemUnitCode: trans.itemUnitCode,
                        itemBarCode: trans.itemBarCode,
                        description1: trans.description1,
                        itemBatchNo: editState.itemBatchNo.trim() || undefined,
                        itemBatchExpDate: editState.itemBatchExpDate ? new Date(editState.itemBatchExpDate) : undefined,
                        itemSerialNo: trans.itemSerialNo,
                        erpLinkName: trans.erpLinkName,
                        shipmentNo: trans.shipmentNo,
                        quantityOrigin: trans.quantityOrigin,
                        itemUnitTxt: trans.itemUnitTxt,
                        lineTypeOrigin: trans.lineTypeOrigin,
                        price: trans.price,
                        currency: trans.currency,
                        reasonCode: trans.reasonCode,
                        transType: trans.transType,
                        sscc: trans.sscc,
                        quantityCorrected: trans.quantityCorrected,
                        freeField1: trans.freeField1,
                        freeField2: trans.freeField2,
                        freeField3: trans.freeField3,
                        appFreeField1: trans.appFreeField1,
                        appFreeField2: trans.appFreeField2,
                        quantityGross: trans.quantityGross,
                        transitPackagingID: trans.transitPackagingID,
                        clientID: trans.clientID,
                        stcBoxNo: trans.stcBoxNo,
                        grShipmentNo: trans.grShipmentNo,
                        externalSystemID: trans.externalSystemID,
                        externalSystemStatus: trans.externalSystemStatus,
                        itemType: trans.itemType,
                        orderNoERP: trans.orderNoERP,
                        lineNoERP: trans.lineNoERP
                    };
                    
                    // Delete old transaction and create new one with updated info
                    await stcTransService.deleteTransaction(trans.batchID, trans.lineID);
                    await stcTransService.createTransaction(trans.batchID, updateRequest);
                    
                    console.log('Batch info updated using fallback delete/create method');
                } else {
                    throw updateError;
                }
            }
            
            // Mark as saved
            setBatchEditState(prev => ({
                ...prev,
                [transId]: {
                    ...prev[transId],
                    isEditing: false,
                    isSaving: false
                }
            }));

            // Reload data to show changes
            await loadOrderData();

            // Re-check if receiving can be finished after updating batch info
            if (batchId) {
                await checkCanFinishReceiving(batchId);
            }

        } catch (err) {
            console.error('Error updating batch info:', err);
            setError(t('warehouse.modules.receipt.errors.updateBatchInfoFailed', { error: err instanceof Error ? err.message : t('common.errors.unknownError', {}, 'Ukjent feil') }, `Kunne ikke oppdatere batch-informasjon: ${err instanceof Error ? err.message : 'Ukjent feil'}`));

            setBatchEditState(prev => ({
                ...prev,
                [transId]: { ...prev[transId], isSaving: false }
            }));
        }
    };

    const handlePrintLabel = async (reportId: number | null, numLabels: number) => {
        if (!selectedSearchLine) {
            setError(t('warehouse.modules.receipt.errors.noItemSelected', {}, 'Ingen vare valgt'));
            return;
        }

        if (!order) {
            setError(t('warehouse.modules.receipt.errors.orderNotLoaded', {}, 'Ordre ikke lastet'));
            return;
        }

        try {
            setError(null);
            const response = await printingService.printLabel(order.companyNo, {
                reportID: reportId,
                numOfLabels: numLabels,
                itemNo: selectedSearchLine.itemNo,
                itemUnitCode: selectedSearchLine.itemUnitCode,
                orderNo: order.orderNo,
                lineNo: selectedSearchLine.lineNo,
                // Add batch info if available (from editingStcTrans or newReceipt)
                batchNo: editingStcTrans?.itemBatchNo,
                expiryDate: editingStcTrans?.itemBatchExpDate
                    ? new Date(editingStcTrans.itemBatchExpDate).toISOString().split('T')[0]
                    : undefined,
                quantity: editingStcTrans?.quantity || newReceipt?.quantity
            });

            if (response.success) {
                // Success - you could show a success message in a success state if needed
                console.log(`${response.labelsProcessed || numLabels} etikett(er) skrevet ut`);
            } else {
                setError(response.message || t('warehouse.modules.receipt.errors.printLabelFailed', {}, 'Feil ved utskrift av etikett'));
            }
        } catch (error: any) {
            console.error('Error printing label:', error);
            setError(error.message || t('warehouse.modules.receipt.errors.printLabelFailed', {}, 'Feil ved utskrift av etikett'));
        }
    };

    const handleStcTransClick = (trans: StcTransDto, orderLine: POrderLineDto) => {
        // Set editing mode for existing StcTrans
        setEditingStcTrans(trans);
        setSelectedSearchLine(orderLine);
        setNewReceipt({
            lineNo: orderLine.lineNo,
            quantity: trans.quantity || 0
        });
    };

    // Enhanced state for batch and expiry from barcode
    const [scannedBatchInfo, setScannedBatchInfo] = useState<{ batchNo?: string; expDate?: string }>({});

    // Detect if any modal is open to disable global scanning
    const isAnyModalOpen = showPutAwayModal || showEditModal || showDirectReceiptModal ||
                           showLineBrowser || showAdvancedConfigModal || isBulkProcessing;

    // Global barcode scanner - processes scans without requiring input focus
    useSimpleBarcodeScanner({
        enabled: !isAnyModalOpen,
        onScan: (barcode: string) => {
            processGlobalBarcode(barcode);
        }
    });

    /**
     * Calculate remaining quantity for a line based on transaction status
     * Remaining = Ordered - Received (status 60) - Arrived (status 20)
     */
    const calculateRemainingQuantity = (line: POrderLineDto, lineNo: number): number => {
        const receivingLine = receivingLines.find(rl => rl.orderLine.lineNo === lineNo);

        if (!receivingLine) {
            // Fallback to backend's remainingQuantity if we don't have transaction data
            return line.remainingQuantity || 0;
        }

        // Calculate totalReceived (status >= 50 - KlarForEksport or Eksportert)
        // Status 50 = direktemottak klar for eksport, Status 60 = eksportert
        const totalReceived = receivingLine.receivedTransactions
            .filter(t => t.lineStatus >= StcTransLineStatus.KlarForEksport)
            .reduce((sum, t) => sum + (t.quantity || 0), 0);

        // Calculate arrivedQuantity (status 20 - UnderArbeid/Ankommet)
        const arrivedQuantity = receivingLine.receivedTransactions
            .filter(t => t.lineStatus === StcTransLineStatus.UnderArbeid)
            .reduce((sum, t) => sum + (t.quantity || 0), 0);

        // Calculate remaining: Ordered - Received - Arrived
        return (line.quantity || 0) - totalReceived - arrivedQuantity;
    };

    /**
     * Validate if receiving quantity would exceed ordered quantity
     * Returns: { allowed: boolean, message: string, isWarning: boolean }
     */
    const validateOverReceiving = (
        orderLine: POrderLineDto,
        newQuantity: number,
        receivingLine?: ReceivingLine
    ): { allowed: boolean; message: string; isWarning: boolean } => {
        console.log('🔍 validateOverReceiving called');
        console.log('  - orderLine:', orderLine);
        console.log('  - newQuantity:', newQuantity);
        console.log('  - receivingLine:', receivingLine);

        const orderedQty = orderLine.quantity || 0;
        console.log('  - orderedQty:', orderedQty);

        // Calculate total that will be received (existing + new)
        // Status >= 50 includes both KlarForEksport (50) and Eksportert (60)
        const totalReceived = receivingLine
            ? receivingLine.receivedTransactions
                .filter(t => t.lineStatus >= StcTransLineStatus.KlarForEksport)
                .reduce((sum, t) => sum + (t.quantity || 0), 0)
            : 0;

        console.log('  - totalReceived (status >= 50):', totalReceived);
        console.log('  - receivingLine.receivedTransactions:', receivingLine?.receivedTransactions);

        const totalAfterReceipt = totalReceived + newQuantity;
        console.log('  - totalAfterReceipt:', totalAfterReceipt);

        // Check if over-receiving
        if (totalAfterReceipt > orderedQty) {
            const overQty = totalAfterReceipt - orderedQty;
            console.log('  ⚠️  OVER-RECEIVING DETECTED!');
            console.log('  - overQty:', overQty);
            console.log('  - userProfile:', userProfile);
            console.log('  - extraQtyOnGRNotAllowed:', userProfile?.extraQtyOnGRNotAllowed);

            // Check profile setting
            if (userProfile?.extraQtyOnGRNotAllowed) {
                console.log('  🚫 BLOCKING over-receive');
                // BLOCK completely
                return {
                    allowed: false,
                    message: t('warehouse.modules.receipt.validation.cannotReceiveMoreThanOrdered', {
                        orderedQty,
                        unit: orderLine.itemUnitTxt || t('warehouse.general.unitDefault', {}, 'stk'),
                        totalReceived,
                        newQuantity,
                        overQty
                    }, `Kan ikke motta mer enn bestilt antall (${orderedQty} ${orderLine.itemUnitTxt || 'stk'}). Allerede mottatt: ${totalReceived} ${orderLine.itemUnitTxt || 'stk'}. Forsøk på å motta: ${newQuantity} ${orderLine.itemUnitTxt || 'stk'} (${overQty} ${orderLine.itemUnitTxt || 'stk'} over).`),
                    isWarning: false
                };
            } else {
                console.log('  ⚠️  WARNING over-receive (allowing)');
                // WARN but allow
                return {
                    allowed: true,
                    message: t('warehouse.modules.receipt.validation.overReceiveWarning', {
                        orderedQty,
                        unit: orderLine.itemUnitTxt || t('warehouse.general.unitDefault', {}, 'stk'),
                        totalAfterReceipt,
                        overQty
                    }, `OBS: Du mottar mer enn bestilt antall. Bestilt: ${orderedQty} ${orderLine.itemUnitTxt || 'stk'}, Totalt mottak vil bli: ${totalAfterReceipt} ${orderLine.itemUnitTxt || 'stk'} (${overQty} ${orderLine.itemUnitTxt || 'stk'} over).`),
                    isWarning: true
                };
            }
        }

        console.log('  ✅ No over-receiving');
        // No over-receiving
        return { allowed: true, message: '', isWarning: false };
    };

    // Process global barcode scan - replicates input box parsing logic
    const processGlobalBarcode = (scanInput: string) => {
        if (!scanInput || !scanInput.trim()) return;

        console.log('🔍 Global scan input:', scanInput);

        // First, remove trailing scanner separators to check for real GS1 structure
        const withoutTrailingSeparators = scanInput.replace(/[~§]+$/g, '').trim();

        // Check if this looks like a GS1-128 structured barcode
        // GS1-128 has separators WITHIN the string (not just trailing), or ]C1 prefix, or parentheses
        const hasInternalSeparators = withoutTrailingSeparators.includes('~') ||
                                       withoutTrailingSeparators.includes('§') ||
                                       withoutTrailingSeparators.includes(String.fromCharCode(29));
        const hasParentheses = scanInput.includes('(');
        const hasC1Prefix = scanInput.startsWith(']C1');

        // A real GS1 barcode has structure indicators, not just trailing separators from scanner
        const looksLikeGS1 = hasInternalSeparators || hasParentheses || hasC1Prefix;

        let gs1Data = null;
        let hasGS1Data = false;

        if (looksLikeGS1) {
            console.log('🔍 GS1 structure detected, attempting GS1 parse...');
            // Try parsing as GS1-128 using the robust parser
            gs1Data = parseGS1Barcode(scanInput);
            console.log('📊 GS1 parse result:', gs1Data);

            // Check if we got any GS1 data
            hasGS1Data = !!(gs1Data.parsedAIs && Object.keys(gs1Data.parsedAIs).length > 0);
            console.log('📊 GS1 parse result - hasGS1Data:', hasGS1Data, 'parsedAIs:', gs1Data?.parsedAIs);
        } else {
            console.log('📝 No GS1 structure detected, treating as plain barcode');
        }

        if (hasGS1Data && gs1Data && gs1Data.gtin) {
            // GS1-128 barcode with GTIN
            console.log('✓ GS1-128 format detected (global scan)');
            handleGS1Scan({
                itemNo: gs1Data.gtin,
                quantity: gs1Data.quantity,
                batchNo: gs1Data.batchNo,
                expiryDate: gs1Data.expiryDate || gs1Data.bestBeforeDate || gs1Data.useByDate,
                sscc: gs1Data.sscc
            });
        } else {
            // Plain barcode (just item number)
            console.log('✓ Plain barcode format detected (global scan)');
            // Strip trailing scanner separators (~ and §) from plain barcodes
            const cleanedItemNo = scanInput.replace(/[~§]+$/g, '').trim();
            console.log('🧹 Cleaned plain barcode:', cleanedItemNo);
            handleGS1Scan({ itemNo: cleanedItemNo });
        }
    };

    const handleGS1Scan = (data: { itemNo?: string; quantity?: number; sscc?: string; batchNo?: string; expiryDate?: string }) => {
        console.log('GS1 Scan data received:', data);

        if (!data.itemNo) {
            console.warn('No itemNo in scan data');
            return;
        }

        // Find matching line in availableOrderLines
        const itemNoLower = data.itemNo.toLowerCase();
        const matchedLine = availableOrderLines.find(
            line => line.itemNo?.toLowerCase() === itemNoLower ||
                    line.itemBarCode?.toLowerCase() === itemNoLower
        );

        if (matchedLine) {
            console.log('Matched order line:', matchedLine);
            console.log('Barcode contains batch info:', { batchNo: data.batchNo, expiryDate: data.expiryDate });

            // Clear editing mode - this is a new scan
            setEditingStcTrans(null);
            setSelectedSearchLine(matchedLine);

            // Set quantity from GS1 data or calculate remaining quantity
            const remainingQty = calculateRemainingQuantity(matchedLine, matchedLine.lineNo);
            const quantity = data.quantity || remainingQty;
            setNewReceipt({
                lineNo: matchedLine.lineNo,
                quantity: quantity
            });

            // Store scanned batch info if present (will be picked up by ReceivingActiveLine)
            setScannedBatchInfo({
                batchNo: data.batchNo,
                expDate: data.expiryDate
            });

            // Auto-focus disabled - global scanning is active
            // setTimeout(() => {
            //     if (quantityInputRef.current) {
            //         quantityInputRef.current.focus();
            //         quantityInputRef.current.select();
            //     }
            // }, 100);
        } else {
            alert(t('warehouse.modules.receipt.messages.itemNotFoundInOrder', {}, 'Vare ikke funnet i ordren!'));
            console.warn('No matching line found for itemNo:', data.itemNo);
        }
    };

    const getStatusColor = (remainingQuantity: number, totalQuantity: number) => {
        if (remainingQuantity === 0) return 'bg-green-100 text-green-800';
        if (remainingQuantity < totalQuantity) return 'bg-yellow-100 text-yellow-800';
        return 'bg-gray-100 text-gray-800';
    };

    const getStatusText = (remainingQuantity: number, totalQuantity: number) => {
        if (remainingQuantity === 0) return t('warehouse.modules.receipt.status.complete', {}, 'Komplett');
        if (remainingQuantity < totalQuantity) return t('warehouse.modules.receipt.status.partiallyReceived', {}, 'Delvis mottatt');
        return t('warehouse.modules.receipt.status.notReceived', {}, 'Ikke mottatt');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex items-center space-x-2">
                    <RefreshCw className="h-6 w-6 animate-spin text-blue-500" />
                    <span className="text-lg text-gray-600">{t('warehouse.modules.receipt.messages.loadingOrderInfo', {}, 'Henter ordre-informasjon...')}</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-red-500" />
                    <span className="text-red-700">{error}</span>
                </div>
            </div>
        );
    }

    if (showSuccessMessage) {
        return (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <div className="flex items-center space-x-2">
                    <CheckCircle className="h-5 w-5 text-green-500" />
                    <div>
                        <h3 className="text-green-800 font-medium">{t('warehouse.modules.receipt.messages.receivingComplete', {}, 'Mottak avsluttet!')}</h3>
                        <p className="text-green-700 text-sm mt-1">
                            {t('warehouse.modules.receipt.messages.returningToList', {}, 'Status er endret til "Klar for eksport". Returnerer til mottakslisten...')}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    if (!order) {
        return (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <div className="flex items-center space-x-2">
                    <AlertCircle className="h-5 w-5 text-yellow-500" />
                    <span className="text-yellow-700">{t('warehouse.modules.receipt.errors.orderNotFound', {}, 'Ordre ikke funnet')}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Mobile/Enterprise scanner support (Zebra, etc.) - invisible input that captures scanner data */}
            <MobileBarcodeCaptureInput
                enabled={!isAnyModalOpen}
                onScan={(barcode) => {
                    console.log('📱 Mobile scanner detected barcode:', barcode);
                    processGlobalBarcode(barcode);
                }}
            />

            {/* Bulk processing overlay */}
            {isBulkProcessing && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-8 max-w-md">
                        <div className="flex items-center space-x-4">
                            <RefreshCw className="h-8 w-8 text-blue-500 animate-spin" />
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900">{t('warehouse.modules.receipt.messages.processingReceipt', {}, 'Prosesserer mottak')}</h3>
                                <p className="text-sm text-gray-600 mt-1">{bulkProcessingMessage}</p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Header - Compact design with buttons on same line as back */}
            <div className="space-y-2">
                {/* Top row: Back button + Action buttons */}
                <div className="flex items-center justify-between">
                    <BackButton onClick={onBack} className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200" />

                    {/* Action buttons on right */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setShowLineBrowser(true)}
                            className="flex items-center gap-1 px-3 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors text-sm"
                            title={t('warehouse.modules.receipt.actions.viewAllLines', {}, 'Se alle ordrelinjer')}
                        >
                            <List className="h-4 w-4" />
                            <span className="hidden sm:inline">{t('warehouse.modules.receipt.labels.lines', {}, 'Linjer')}</span>
                        </button>
                        <button
                            onClick={() => loadOrderData()}
                            className="flex items-center gap-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm"
                            title={t('common.buttons.refresh', {}, 'Oppdater')}
                        >
                            <RefreshCw className="h-4 w-4" />
                        </button>

                        {hasTransactions && (
                            <>
                                <button
                                    onClick={handleFinishReceiving}
                                    disabled={!canFinishReceiving || isFinishingReceiving}
                                    className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors text-sm ${
                                        canFinishReceiving && !isFinishingReceiving
                                            ? 'bg-green-500 text-white hover:bg-green-600'
                                            : 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                    }`}
                                    title={!canFinishReceiving ? t('warehouse.modules.receipt.messages.cannotFinishYet', {}, 'Mottaket kan ikke avsluttes ennå') : t('warehouse.modules.receipt.actions.finishReceiving', {}, 'Avslutt mottak')}
                                >
                                    {isFinishingReceiving ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <Check className="h-4 w-4" />
                                    )}
                                    <span className="hidden sm:inline">{isFinishingReceiving ? t('warehouse.modules.receipt.actions.finishing', {}, 'Avslutter...') : t('warehouse.modules.receipt.actions.finish', {}, 'Avslutt')}</span>
                                </button>

                                <button
                                    onClick={handleFinishReceivingAndPutAway}
                                    disabled={!canFinishReceiving || isFinishingReceiving}
                                    className={`flex items-center gap-1 px-3 py-2 rounded-lg transition-colors text-sm ${
                                        canFinishReceiving && !isFinishingReceiving
                                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                                            : 'bg-gray-400 text-gray-700 cursor-not-allowed'
                                    }`}
                                    title={!canFinishReceiving ? t('warehouse.modules.receipt.messages.cannotFinishYet', {}, 'Mottaket kan ikke avsluttes ennå') : t('warehouse.modules.receipt.actions.finishAndPutAway', {}, 'Avslutt og gå til plassering')}
                                >
                                    {isFinishingReceiving ? (
                                        <RefreshCw className="h-4 w-4 animate-spin" />
                                    ) : (
                                        <ArrowRight className="h-4 w-4" />
                                    )}
                                    <span className="hidden sm:inline">{isFinishingReceiving ? t('warehouse.modules.receipt.actions.processing', {}, 'Prosesserer...') : t('warehouse.modules.receipt.actions.receiptAndPutAway', {}, 'Mottak & Plassering')}</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Order info below buttons, aligned left */}
                <div className="ml-0">
                    <h1 className="text-lg font-bold text-gray-900">
                        {t('warehouse.modules.receipt.orderHeader', { orderNo: order.orderNoERP }, `Ordre ${order.orderNoERP}`)}{order.supplierOrderNo && ` | ${order.supplierOrderNo}`}
                    </h1>
                    <p className="text-sm text-gray-600">{order.supplierName}</p>
                </div>
            </div>

            {/* GS1 Scanner - Enhanced with full GS1-128 and plain barcode support */}
            <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
                <div className="flex gap-2">
                    <input
                        ref={scannerRef}
                        type="text"
                        placeholder={t('warehouse.modules.receipt.placeholders.scanBarcode', {}, 'Skann strekkode (GS1-128 eller vanlig varenummer)')}
                        className="flex-1 p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                const target = e.target as HTMLInputElement;
                                const value = target.value.trim();
                                if (value) {
                                    console.log('Raw scan input:', value);

                                    // First, remove trailing scanner separators to check for real GS1 structure
                                    const withoutTrailingSeparators = value.replace(/[~§]+$/g, '').trim();

                                    // Check if this looks like a GS1-128 structured barcode
                                    // GS1-128 has separators WITHIN the string (not just trailing), or ]C1 prefix, or parentheses
                                    const hasInternalSeparators = withoutTrailingSeparators.includes('~') ||
                                                                   withoutTrailingSeparators.includes('§') ||
                                                                   withoutTrailingSeparators.includes(String.fromCharCode(29));
                                    const hasParentheses = value.includes('(');
                                    const hasC1Prefix = value.startsWith(']C1');

                                    // A real GS1 barcode has structure indicators, not just trailing separators from scanner
                                    const looksLikeGS1 = hasInternalSeparators || hasParentheses || hasC1Prefix;

                                    let gs1Data = null;
                                    let hasGS1Data = false;

                                    if (looksLikeGS1) {
                                        console.log('🔍 GS1 structure detected, attempting GS1 parse...');
                                        // Try parsing as GS1-128 first using the robust parser
                                        gs1Data = parseGS1Barcode(value);
                                        console.log('📊 GS1 parse result:', gs1Data);

                                        // Check if we got any GS1 data
                                        hasGS1Data = !!(gs1Data.parsedAIs && Object.keys(gs1Data.parsedAIs).length > 0);
                                        console.log('📊 GS1 parse result - hasGS1Data:', hasGS1Data, 'parsedAIs:', gs1Data?.parsedAIs);
                                    } else {
                                        console.log('📝 No GS1 structure detected, treating as plain barcode');
                                    }

                                    if (hasGS1Data && gs1Data && gs1Data.gtin) {
                                        // GS1-128 barcode with GTIN
                                        console.log('✓ GS1-128 format detected');
                                        handleGS1Scan({
                                            itemNo: gs1Data.gtin,
                                            quantity: gs1Data.quantity,
                                            batchNo: gs1Data.batchNo,
                                            expiryDate: gs1Data.expiryDate || gs1Data.bestBeforeDate || gs1Data.useByDate,
                                            sscc: gs1Data.sscc
                                        });
                                    } else {
                                        // Plain barcode (just item number)
                                        console.log('✓ Plain barcode format detected');
                                        // Strip trailing scanner separators (~ and §) from plain barcodes
                                        const cleanedItemNo = value.replace(/[~§]+$/g, '').trim();
                                        console.log('🧹 Cleaned plain barcode:', cleanedItemNo);
                                        handleGS1Scan({ itemNo: cleanedItemNo });
                                    }

                                    target.value = '';
                                }
                            }
                        }}
                    />
                </div>
                {/*<div className="text-xs text-gray-500 mt-2">*/}
                {/*    Støtter både GS1-128 (med batch/utløpsdato) og vanlige strekkoder*/}
                {/*</div>*/}
            </div>

            {/* Warning Message */}
            {warningMessage && (
                <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4 mb-4">
                    <div className="flex items-center">
                        <AlertCircle className="h-5 w-5 text-yellow-500 mr-2" />
                        <p className="text-yellow-700">{warningMessage}</p>
                    </div>
                </div>
            )}

            {/* Active Line Component */}
            {selectedSearchLine && newReceipt && (() => {
                // Calculate quantities from transactions for the selected line
                const receivingLine = receivingLines.find(rl => rl.orderLine.lineNo === newReceipt.lineNo);

                // Only sum transactions with status = 20 (UnderArbeid) for arrivedQuantity
                const arrivedQuantity = receivingLine
                    ? receivingLine.receivedTransactions
                        .filter(t => t.lineStatus === StcTransLineStatus.UnderArbeid)
                        .reduce((sum, t) => sum + (t.quantity || 0), 0)
                    : 0;

                // Sum transactions with status >= 50 (KlarForEksport or Eksportert) for totalReceived
                // Status 50 = direktemottak klar for eksport, Status 60 = eksportert
                const totalReceived = receivingLine
                    ? receivingLine.receivedTransactions
                        .filter(t => t.lineStatus >= StcTransLineStatus.KlarForEksport)
                        .reduce((sum, t) => sum + (t.quantity || 0), 0)
                    : 0;

                // Calculate remainingQuantity: Ordered - Received - Arrived
                const remainingQuantity = (selectedSearchLine.quantity || 0) - totalReceived - arrivedQuantity;

                return (
                    <ReceivingActiveLine
                        line={{
                            itemNo: selectedSearchLine.itemNo,
                            description1: selectedSearchLine.description1 || t('common.noDescription', {}, 'Ingen beskrivelse'),
                            quantity: selectedSearchLine.quantity || 0,
                            totalReceived: totalReceived,
                            arrivedQuantity: arrivedQuantity,
                            remainingQuantity: remainingQuantity,
                            itemUnitTxt: selectedSearchLine.itemUnitTxt || t('warehouse.general.unitDefault', {}, 'stk'),
                            itemType: selectedLineItemType
                        }}
                        editingStcTrans={editingStcTrans ? {
                            itemBatchNo: editingStcTrans.itemBatchNo || '',
                            itemBatchExpDate: editingStcTrans.itemBatchExpDate ? new Date(editingStcTrans.itemBatchExpDate).toISOString().split('T')[0] : '',
                            quantity: editingStcTrans.quantity || 0,
                            itemType: editingStcTrans.itemType
                        } : null}
                        currentQuantity={editingStcTrans ? editingStcTrans.quantity || 0 : newReceipt.quantity}
                        scannedBatchInfo={scannedBatchInfo}
                        validationError={validationError}
                        validationWarning={validationWarning}
                        onQuantityChange={(qty) => {
                            // Clear validation when quantity changes
                            setValidationError('');
                            setValidationWarning('');

                            if (editingStcTrans) {
                                // When editing, update the editingStcTrans object directly
                                setEditingStcTrans({ ...editingStcTrans, quantity: qty });
                            } else {
                                // When creating new, update newReceipt
                                setNewReceipt({ ...newReceipt, quantity: qty });
                            }
                        }}
                        onBatchInfoChange={(batchNo, expDate) => {
                            if (editingStcTrans) {
                                setEditingStcTrans({
                                    ...editingStcTrans,
                                    itemBatchNo: batchNo,
                                    itemBatchExpDate: expDate ? new Date(expDate) : undefined
                                });
                            }
                            // For new receipts, batch info is handled internally in ReceivingActiveLine
                        }}
                        onSave={(qty, batchNo, expDate, receivingLocation) => {
                            handleAddReceipt(newReceipt.lineNo, qty, batchNo, expDate, receivingLocation);
                            // Clear scanned batch info after save
                            setScannedBatchInfo({});
                        }}
                        onCancel={() => {
                            setNewReceipt(null);
                            setSelectedSearchLine(null);
                            setEditingStcTrans(null);
                            setScannedBatchInfo({});
                            setValidationError('');
                            setValidationWarning('');
                        }}
                        onDirectReceipt={editingStcTrans
                            ? (qty, batchNo, expDate, receivingLocation) => {
                                // For editing existing: just open modal with current StcTrans
                                // (qty, batchNo, expDate from component are already in editingStcTrans via onBatchInfoChange/onQuantityChange)
                                handleDirectReceipt(editingStcTrans);
                            }
                            : (qty, batchNo, expDate, receivingLocation) => {
                                // For new receipts: create StcTrans first, then open direktemottak modal
                                handleDirectReceiptFromNew(newReceipt.lineNo, qty, batchNo, expDate, receivingLocation);
                                // Clear scanned batch info after direct receipt
                                setScannedBatchInfo({});
                            }
                        }
                        onDelete={editingStcTrans ? () => handleDeleteReceipt(editingStcTrans.lineID, editingStcTrans.id!) : undefined}
                        showImage={false}
                        onOpenGrunndata={allowAdvItemConfig ? () => handleOpenAdvancedConfig(selectedSearchLine.itemNo) : undefined}
                        onPrintLabel={(reportId, numLabels) => handlePrintLabel(reportId, numLabels)}
                        showPrintSection={true}
                        companyNo={order.companyNo}
                        warehouseID={order.warehouse || 'MAIN'}
                        defaultReceivingLocation={userProfile?.defaultGRBinCode}
                    />
                );
            })()}

            {/* Combined Search Box replaced with GS1Scanner + ActiveLine above */}

            {/* Table layout with sticky header */}
            {(receivingLines.some(line => line.receivedTransactions.length > 0) || newReceipt) && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            {/* Sticky header */}
                            <thead className="bg-blue-100 sticky top-0 z-10">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.general.itemNumber', {}, 'Varenr')}</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.ordered', {}, 'Bestilt')}</th>
                                    <th className="px-4 py-3 text-right text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.arrived', {}, 'Ankommet')}</th>
                                    <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.arrivalLocation', {}, 'Ankomstlokasjon')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Active (unlocked) lines */}
                                {(() => {
                                    // Flatten all active transactions first to get proper sequential index
                                    const activeRows = receivingLines.flatMap((receivingLine) => {
                                        if (receivingLine.receivedTransactions.length > 0) {
                                            return receivingLine.receivedTransactions
                                                .filter(trans => trans.lineStatus < 50)
                                                .map((trans) => ({ receivingLine, trans }));
                                        }
                                        return [];
                                    });

                                    return activeRows.map((row, index) => {
                                        const { receivingLine, trans } = row;

                                        // Initialize batch edit state
                                        initializeBatchEdit(trans);
                                        const transId = `${trans.id}`;
                                        const cachedEditState = batchEditState[transId];

                                        // Get arrived quantity (transaction quantity)
                                        const arrivedQty = cachedEditState?.isEditingQuantity ? cachedEditState.quantity : (trans.quantity || 0);

                                        // Get ordered quantity
                                        const orderedQty = receivingLine.orderLine.quantity || 0;

                                        // Alternating row colors - index 0 = white, index 1 = gray, etc.
                                        const bgColor = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';

                                        return (
                                            <tr
                                                key={`${receivingLine.orderLine.lineNo}-${trans.id}`}
                                                className={`${bgColor} hover:bg-blue-50 cursor-pointer transition-colors`}
                                                onClick={() => handleStcTransClick(trans, receivingLine.orderLine)}
                                            >
                                                <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                                                    {receivingLine.orderLine.itemNo}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700">
                                                    {receivingLine.orderLine.description1 || t('common.noDescription', {}, 'Ingen beskrivelse')}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-right font-medium text-gray-900 whitespace-nowrap">
                                                    {orderedQty} <span className="font-normal text-gray-500">{receivingLine.orderLine.itemUnitTxt}</span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-right font-medium text-blue-600 whitespace-nowrap">
                                                    {arrivedQty} <span className="font-normal text-gray-500">{receivingLine.orderLine.itemUnitTxt}</span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                                                    {trans.locationName || '-'}
                                                </td>
                                            </tr>
                                        );
                                    });
                                })()}
                            </tbody>
                        </table>
                    </div>

                    {/* Locked lines panel (collapsed by default) */}
                    {receivingLines.some(line => line.receivedTransactions.some(trans => trans.lineStatus >= 50)) && (
                        <div className="border-t border-gray-200">
                            <button
                                onClick={() => setShowLockedLines(!showLockedLines)}
                                className="flex items-center justify-between w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 transition-colors"
                            >
                                <div className="flex items-center space-x-2">
                                    <Package className="h-5 w-5 text-gray-600" />
                                    <span className="font-medium text-gray-700">
                                        {t('warehouse.modules.receipt.labels.completedReceipts', { count: receivingLines.flatMap(line => line.receivedTransactions.filter(trans => trans.lineStatus >= 50)).length }, `Fullførte mottak (${receivingLines.flatMap(line => line.receivedTransactions.filter(trans => trans.lineStatus >= 50)).length})`)}
                                    </span>
                                </div>
                                {showLockedLines ? (
                                    <ChevronDown className="h-5 w-5 text-gray-600" />
                                ) : (
                                    <ChevronRight className="h-5 w-5 text-gray-600" />
                                )}
                            </button>

                            {showLockedLines && (() => {
                                // Group locked transactions by orderNo, lineNo, itemNo
                                const groupedLines = receivingLines
                                    .filter(rl => rl.receivedTransactions.some(t => t.lineStatus >= 50))
                                    .map(receivingLine => ({
                                        key: `${receivingLine.orderLine.orderNo}-${receivingLine.orderLine.lineNo}-${receivingLine.orderLine.itemNo}`,
                                        orderLine: receivingLine.orderLine,
                                        // Count of locked transactions for this line
                                        transactionCount: receivingLine.receivedTransactions.filter(t => t.lineStatus >= 50).length
                                    }));

                                // Remove duplicates (same order line might appear multiple times)
                                const uniqueLines = groupedLines.filter((line, index, self) =>
                                    index === self.findIndex(l => l.key === line.key)
                                );

                                return (
                                    <table className="min-w-full">
                                        {/* Header for locked lines */}
                                        <thead className="bg-blue-100">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.general.itemNumber', {}, 'Varenr')}</th>
                                                <th className="px-4 py-3 text-left text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('common.fields.description', {}, 'Beskrivelse')}</th>
                                                <th className="px-4 py-3 text-right text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.ordered', {}, 'Bestilt')}</th>
                                                <th className="px-4 py-3 text-right text-xs font-semibold text-blue-800 uppercase tracking-wider">{t('warehouse.modules.receipt.fields.received', {}, 'Mottatt')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {uniqueLines.map((line, index) => {
                                                // Alternating row colors
                                                const bgColor = index % 2 === 0 ? 'bg-white' : 'bg-gray-50';

                                                // Get quantities
                                                const orderedQty = line.orderLine.quantity || 0;
                                                const deliveredQty = line.orderLine.quantityDelivered || 0;

                                                return (
                                                    <tr key={line.key} className={bgColor}>
                                                        <td className="px-4 py-3 text-sm font-medium text-gray-700 whitespace-nowrap">
                                                            {line.orderLine.itemNo}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-gray-600">
                                                            {line.orderLine.description1 || t('common.noDescription', {}, 'Ingen beskrivelse')}
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-right font-medium text-gray-700 whitespace-nowrap">
                                                            {orderedQty} <span className="font-normal text-gray-500">{line.orderLine.itemUnitTxt}</span>
                                                        </td>
                                                        <td className="px-4 py-3 text-sm text-right font-medium text-green-600 whitespace-nowrap">
                                                            {deliveredQty} <span className="font-normal text-gray-500">{line.orderLine.itemUnitTxt}</span>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                );
                            })()}
                        </div>
                    )}
                </div>
            )}

            {/* Melding når ingen linjer har StcTrans */}
            {!receivingLines.some(line => line.receivedTransactions.length > 0) && !newReceipt && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                    <div className="flex items-center space-x-2">
                        <Package className="h-5 w-5 text-blue-500" />
                        <div>
                            <h3 className="text-blue-800 font-medium">{t('warehouse.modules.receipt.messages.noReceivedItemsYet', {}, 'Ingen mottatte varer ennå')}</h3>
                            <p className="text-blue-700 text-sm mt-1">
                                {t('warehouse.modules.receipt.messages.startReceivingInstructions', {}, 'Bruk søkefeltet ovenfor eller "Alle ordrelinjer" for å starte mottaket. Mottatte varer vil vises her med detaljerte StcTrans oppføringer.')}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Line browser modal */}
            {showLineBrowser && (
                <POrderLineBrowser
                    orderNo={orderNo}
                    orderLines={availableOrderLines}
                    onLineSelect={(line) => {
                        // Clear editing mode - this is selecting a new line
                        setEditingStcTrans(null);
                        setSelectedSearchLine(line);
                        const remainingQty = calculateRemainingQuantity(line, line.lineNo);
                        setNewReceipt({ lineNo: line.lineNo, quantity: remainingQty });
                        setShowLineBrowser(false);
                    }}
                    onAllLinesSelect={async (lines) => {
                        // Bulk-registrering av alle valgte linjer med bestilt antall
                        setShowLineBrowser(false);

                        // Sett prosesseringstilstand
                        setIsBulkProcessing(true);
                        setBulkProcessingMessage(t('warehouse.modules.receipt.messages.registeringLines', { count: lines.length }, `Registrerer ${lines.length} linjer automatisk...`));
                        setError(null);

                        // Kjør bulk-registrering i bakgrunnen
                        setTimeout(async () => {
                            try {
                                // Hent profil én gang for alle linjer
                                let locationName = '';
                                try {
                                    const profile = await userProfileService.getCurrentUserProfile();
                                    locationName = profile.defaultGRBinCode || '';
                                } catch (profileError) {
                                    console.warn('Kunne ikke hente profil for LocationName:', profileError);
                                }

                                // Ensure TransOrder exists once
                                if (order) {
                                    await stcTransService.ensureTransOrderExists(batchId, order);
                                }

                                // Forbered alle transaksjoner
                                const transactions: CreateStcTransRequest[] = lines
                                    .filter(line => (line.quantity || 0) > 0)
                                    .map((line) => {
                                        const quantityToReceive = line.quantity || 0;
                                        const erpLinkName = order.orderNoERP + '-' + line.lineNoERP;

                                        return {
                                            batchID: batchId,
                                            lineType: 0,
                                            orderNo: orderNo,
                                            lineNo: line.lineNo,
                                            locationName: locationName,
                                            quantity: quantityToReceive,
                                            transDt: new Date(),
                                            warehouse: order.warehouse || 'MAIN',
                                            toWarehouse: order.warehouse,
                                            operatorID: '0',
                                            itemNo: line.itemNo,
                                            itemBarCode: line.itemBarCode,
                                            description1: line.description1,
                                            itemUnitCode: line.itemUnitCode,
                                            itemUnitTxt: line.itemUnitTxt,
                                            transType: 0,
                                            orderNoERP: order.orderNoERP,
                                            lineNoERP: line.lineNoERP,
                                            erpLinkName: erpLinkName,
                                            // itemType hentes separat per vare og er ikke tilgjengelig på POrderLineDto
                                            itemBatchNo: '',
                                            itemBatchExpDate: undefined,
                                            lineStatus: StcTransLineStatus.UnderArbeid
                                        };
                                    });

                                // Kall bulk-endpoint
                                await stcTransService.createTransactionsBulk(batchId, transactions);

                                // Vis suksessmelding
                                setBulkProcessingMessage(t('warehouse.modules.receipt.messages.linesRegistered', { count: lines.length }, `${lines.length} linjer registrert!`));

                                // Oppdater data og lukk prosesseringsindikator
                                await loadOrderData();

                                // Re-check if receiving can be finished after bulk registration
                                if (batchId) {
                                    await checkCanFinishReceiving(batchId);
                                }

                                setTimeout(() => {
                                    setIsBulkProcessing(false);
                                    setShowSuccessMessage(true);
                                    setTimeout(() => setShowSuccessMessage(false), 3000);
                                }, 500);

                            } catch (error) {
                                console.error('Feil ved bulk-registrering:', error);
                                setError(t('warehouse.modules.receipt.errors.bulkRegistrationFailed', {}, 'Feil ved registrering av linjer. Se konsollen for detaljer.'));
                                setIsBulkProcessing(false);
                            }
                        }, 100);
                    }}
                    onClose={() => setShowLineBrowser(false)}
                />
            )}

            {/* Put-Away Modal */}
            {showPutAwayModal && selectedStcTrans && order && (
                <PutAwayModal
                    isOpen={showPutAwayModal}
                    onClose={handleModalClose}
                    stcTrans={selectedStcTrans}
                    companyNo={order.companyNo}
                    warehouseID={order.warehouse || 'MAIN'}
                    onSuccess={handleModalSuccess}
                />
            )}

            {/* Edit StcTrans Modal */}
            {showEditModal && selectedStcTrans && (
                <StcTransEditModal
                    isOpen={showEditModal}
                    onClose={handleModalClose}
                    stcTrans={selectedStcTrans}
                    onSuccess={handleModalSuccess}
                />
            )}

            {/* Direct Receipt Modal */}
            {showDirectReceiptModal && selectedStcTrans && order && (
                <DirectReceiptModal
                    isOpen={showDirectReceiptModal}
                    onClose={handleModalClose}
                    stcTrans={selectedStcTrans}
                    companyNo={order.companyNo}
                    warehouseID={order.warehouse || 'MAIN'}
                    onSuccess={handleModalSuccess}
                />
            )}

            {/* Advanced Item Config Modal */}
            {showAdvancedConfigModal && advancedConfigItemNo && order && (
                <AdvancedItemConfigModal
                    isOpen={showAdvancedConfigModal}
                    onClose={handleCloseAdvancedConfig}
                    companyNo={order.companyNo}
                    itemNo={advancedConfigItemNo}
                    onSave={handleAdvancedConfigSaved}
                />
            )}

            {/* Over-Receive Confirmation Modal */}
            <OverReceiveConfirmModal
                isOpen={showOverReceiveConfirmModal}
                message={overReceiveModalMessage}
                onConfirm={handleConfirmOverReceive}
                onCancel={handleCancelOverReceive}
            />
        </div>
    );
};